import * as SwitchPrimitives from '@radix-ui/react-switch'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { TailwindColor, tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import * as React from 'react'

import Loader from './loader'
import Typography, { TextType } from './typography'

export type SwitchVariant = 'primary' | 'danger' | 'secondary' | 'success'

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> & {
    variant?: SwitchVariant
    label?: string
    labelVariant?: TextType
    color?: TailwindColor
    withBorder?: boolean
    borderClassName?: string
    isLoading?: boolean
  }
>(
  (
    {
      className,
      variant = 'primary',
      color = tailwindColors.grey.DEFAULT,
      label,
      labelVariant = 'label',
      withBorder = false,
      borderClassName,
      isLoading = false,
      ...props
    },
    ref
  ) => {
    return (
      <div
        className={cn(
          'border border-grey-stroke rounded w-fit p-1 flex gap-1 items-center',
          {
            'border-primary': props.checked && withBorder,
            'border-none': !withBorder,
            'pointer-events-none': isLoading
          },
          borderClassName
        )}
      >
        <SwitchPrimitives.Root
          className={cn(
            'peer inline-flex h-7 w-12 shrink-0  cursor-pointer items-center rounded-full  transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=unchecked]:bg-input',
            {
              'bg-red-light-active data-[state=checked]:bg-red': variant === 'danger',
              'bg-grey-light data-[state=checked]:bg-primary': variant === 'primary',
              'bg-primary-light-active data-[state=checked]:bg-primary': variant === 'secondary',
              'bg-green-light-active data-[state=checked]:bg-green': variant === 'success'
            },
            className
          )}
          {...props}
          ref={ref}
        >
          <SwitchPrimitives.Thumb
            className={cn(
              'pointer-events-none block h-5 w-5 rounded-full shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-6 data-[state=unchecked]:translate-x-1 ',
              {
                'bg-red data-[state=checked]:bg-red-light-active': variant === 'danger',
                'bg-white data-[state=checked]:bg-grey-light': variant === 'primary',
                'bg-primary data-[state=checked]:bg-primary-light-active': variant === 'secondary',
                'bg-green data-[state=checked]:bg-green-light-active': variant === 'success'
              }
            )}
          >
            {isLoading && <Loader size="sm" />}
          </SwitchPrimitives.Thumb>
        </SwitchPrimitives.Root>
        {label && (
          <Typography variant={labelVariant || 'label'} color={color}>
            {label}
          </Typography>
        )}
      </div>
    )
  }
)
Switch.displayName = SwitchPrimitives.Root.displayName

export default Switch
